import React, { useEffect, useState } from 'react'
import RatingBox from '../../components/ui/RatingBox'
import { ConfirmDelete, MwButton, MwTextArea } from '../../components/ui'
import { useTranslation } from 'react-i18next'
import usePost from '../../hooks/usePost'
import usePut from '../../hooks/usePut'
import useFetch from '../../hooks/useFetch'
import useDelete from '../../hooks/useDelete'

const RatingListItem = ({item,index,onDeleteSuccess}) => {
    const [t,i18n] = useTranslation('global')
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [showComplaintModal, setShowComplaintModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [historyPage, setHistoryPage] = useState(1)
    const [replyText, setReplyText] = useState('')
    const { data: replyResult, loading: replyLoading, postData } = usePost()
    const { data: statusResult, loading: statusLoading, putData } = usePut()
    const { data: deleteResult, loading: deleteLoading, deleteItem } = useDelete()
    const historyUrl = `${process.env.REACT_APP_RATING_API_BASE_URL}/complaints/${item?._id}/replies?page=${historyPage}&limit=15`
    const { data: historyResult, loading: historyLoading, refreshHandler: refreshHistory } = useFetch(historyUrl, false)
    const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
    const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-US';

    const currentDate = new Date(item?.createdAt)
    const currentTime = currentDate.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
    })
    const locationValue = (value) => value && String(value).trim().length > 0 ? value : '-'
    const hasComplaint = item?.customerComplaint && item.customerComplaint.trim().length > 0
    const [complaintStatus, setComplaintStatus] = useState(item?.complaintStatus || (hasComplaint ? 'incomplete' : 'completed'))
    const isIncomplete = complaintStatus === 'incomplete'
    const ratingType = item?.ratingType || (hasComplaint ? 'complaint' : 'rating')
    const ratingTypeLabel = ratingType === 'complaint' ? t('rating.typeComplaint') : t('rating.typeRating')

    useEffect(() => {
        const nextStatus = item?.complaintStatus || ((item?.customerComplaint || '').trim().length > 0 ? 'incomplete' : 'completed')
        setComplaintStatus(nextStatus)
        setShowDetailsModal(false)
        setShowHistory(false)
        setHistoryPage(1)
    }, [item])

    useEffect(() => {
        if (replyResult?.data) {
            setReplyText('')
            if (showHistory) {
                refreshHistory()
            }
        }
    }, [replyResult, showHistory])

    useEffect(() => {
        if (statusResult?.data) {
            const nextStatus = statusResult.data.complaintStatus || ((item?.customerComplaint || '').trim().length > 0 ? 'incomplete' : 'completed')
            setComplaintStatus(nextStatus)
        }
    }, [statusResult, item?.customerComplaint])

    useEffect(() => {
        if (showComplaintModal && showHistory && item?._id) {
            refreshHistory()
        }
    }, [showComplaintModal, showHistory, historyPage, item?._id])

    useEffect(() => {
        if (deleteResult?.status === 1) {
            setShowDeleteModal(false)
            if (onDeleteSuccess) {
                onDeleteSuccess()
            }
        }
    }, [deleteResult, onDeleteSuccess])

    const submitReplyHandler = () => {
        const trimmedReply = replyText.trim()
        if (!trimmedReply) return
        postData(`${process.env.REACT_APP_RATING_API_BASE_URL}/complaints/${item._id}`, {
            reply: trimmedReply
        })
    }

    const updateStatusHandler = (status) => {
        if (!item?._id) return
        putData(`${process.env.REACT_APP_RATING_API_BASE_URL}/complaints/${item._id}/status`, { status })
    }

    const openHistoryModal = () => {
        setHistoryPage(1)
        setShowHistory(true)
        setShowComplaintModal(true)
    }

    const deleteRatingHandler = () => {
        if (!item?._id) return
        deleteItem(`${process.env.REACT_APP_RATING_API_BASE_URL}/${item._id}`)
    }

    const historyReplies = historyResult?.data || []
    const historyPagination = historyResult?.pagination
    const historyTotalPages = historyPagination?.totalPages || 1
    const historyCurrentPage = historyPagination?.page || historyPage

    return (
        <>
            <div className='grid grid-cols-[40px_1.5fr_1.5fr_1.8fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_0.9fr] gap-2 items-center px-3 py-2 border-b text-slate-600'>
                <div className='text-center'>{index+1}</div>
                <div className='text-center whitespace-nowrap'> <RatingBox rate={item.ratingService}/></div>
                <div className='text-center whitespace-nowrap'> <RatingBox rate={item.ratingEmployee }/></div>
                <div className='text-center text-[11px]'>
                    <span>{currentDate.toLocaleDateString(locale, options)}</span>
                    <span className='mx-1'>-</span>
                    <span>{currentTime}</span>
                </div>
                <div className='text-center truncate'>{locationValue(item.customerIp)}</div>
                <div className='text-center truncate'>{locationValue(item.customerCity)}</div>
                <div className='text-center truncate'>{locationValue(item.customerCountry)}</div>
                <div className='text-center'>
                    {hasComplaint ? (
                        <button
                            type='button'
                            onClick={() => setShowComplaintModal(true)}
                            className={`${isIncomplete ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'} text-[10px] font-bold px-2 py-1 rounded-full`}
                        >
                            {isIncomplete ? t('rating.incomplete') : t('rating.completed')}
                        </button>
                    ) : (
                        <span className='bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-1 rounded-full'>
                            {t('rating.completed')}
                        </span>
                    )}
                </div>
                <div className='text-center'>
                    <div className='flex flex-col items-center gap-1'>
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${hasComplaint ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                            {ratingTypeLabel}
                        </span>
                    </div>
                </div>
                <div className='text-center'>
                    <button
                        type='button'
                        onClick={() => setShowDetailsModal(true)}
                        className='text-[10px] font-bold px-3 py-1 rounded-full border border-slate-300 text-slate-700 bg-slate-50 hover:bg-slate-100'
                    >
                        {t('rating.details')}
                    </button>
                </div>
                <div className='text-center'>
                    {hasComplaint ? (
                        <button
                            type='button'
                            onClick={openHistoryModal}
                            className='text-[10px] font-bold px-3 py-1 rounded-full border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'
                        >
                            {t('rating.complaintsHistory')}
                        </button>
                    ) : (
                        <span className='text-slate-300'>-</span>
                    )}
                </div>
                <div className='text-center'>
                    <button
                        type='button'
                        onClick={() => setShowDeleteModal(true)}
                        className='text-[10px] font-bold px-3 py-1 rounded-full border border-red-200 text-red-700 bg-red-50 hover:bg-red-100'
                    >
                        {t('common.delete')}
                    </button>
                </div>
            </div>
            <ConfirmDelete
                title={t('common.confirmDelete')}
                msg={t('common.confirmDelete')}
                onShow={showDeleteModal}
                setOnShow={setShowDeleteModal}
                loading={deleteLoading}
                onDeleteHandler={deleteRatingHandler}
            />
            {showDetailsModal && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
                    <div className='w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden'>
                        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
                            <div className='text-sm font-bold text-slate-700'>{t('rating.details')}</div>
                            <button
                                type='button'
                                onClick={() => setShowDetailsModal(false)}
                                className='text-xs text-slate-500 hover:text-slate-700'
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                        <div className='p-6 space-y-3'>
                            <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                                <div className='text-[11px] text-slate-500 mb-1'>{t('rating.betterService')}</div>
                                <div className='text-xs text-slate-700'>{item.HowCanWeBestServeYou || '-'}</div>
                            </div>
                            <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                                <div className='text-[11px] text-slate-500 mb-1'>{t('rating.pricesReasonable')}</div>
                                <div className='text-xs text-slate-700'>{item.AreThePricesReasonable || (item.ratingService > 3 ? t('common.yes') : '-')}</div>
                            </div>
                            <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                                <div className='text-[11px] text-slate-500 mb-1'>{t('rating.mobileNumber')}</div>
                                <div className='text-xs text-slate-700'>{item.customerMobile || '-'}</div>
                            </div>
                            <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                                <div className='text-[11px] text-slate-500 mb-1'>{t('rating.complaint')}</div>
                                <div className='text-xs text-slate-700'>{item.customerComplaint || '-'}</div>
                            </div>
                            <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-3'>
                                <div className='text-[11px] text-slate-500 mb-1'>{t('rating.ratingDate')}</div>
                                <div className='text-xs text-slate-700'>{currentDate.toLocaleDateString(locale, options)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasComplaint && showComplaintModal && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
                    <div className='w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden'>
                        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
                            <div className='flex items-center gap-3'>
                                <div className='text-sm font-bold text-slate-700'>{t('rating.replyToComplaint')}</div>
                                <span className={`${isIncomplete ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'} text-[10px] font-bold px-2 py-1 rounded-full`}>
                                    {isIncomplete ? t('rating.incomplete') : t('rating.completed')}
                                </span>
                            </div>
                            <button
                                type='button'
                                onClick={() => {
                                    setShowComplaintModal(false)
                                    setShowHistory(false)
                                    setHistoryPage(1)
                                }}
                                className='text-xs text-slate-500 hover:text-slate-700'
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                        <div className='p-6 space-y-4'>
                            <div className='bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2'>
                                <div className='flex items-center justify-between text-[10px] text-slate-500'>
                                    <span className='font-medium'>{t('rating.complaint')}</span>
                                    <span>{currentDate.toLocaleDateString(locale, options)}</span>
                                </div>
                                <div className='text-xs text-slate-700 leading-6'>
                                    {item.customerComplaint}
                                </div>
                            </div>
                            <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2'>
                                <div className='text-xs font-medium text-slate-600'>{t('rating.status')}</div>
                                <div className='flex items-center gap-2'>
                                    <button
                                        type='button'
                                        onClick={() => updateStatusHandler('completed')}
                                        disabled={statusLoading || complaintStatus === 'completed'}
                                        className={`text-[10px] font-bold px-3 py-1 rounded-full border ${complaintStatus === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-200'}`}
                                    >
                                        {t('rating.markCompleted')}
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => updateStatusHandler('incomplete')}
                                        disabled={statusLoading || complaintStatus === 'incomplete'}
                                        className={`text-[10px] font-bold px-3 py-1 rounded-full border ${complaintStatus === 'incomplete' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-white text-slate-500 border-slate-200 hover:border-amber-200'}`}
                                    >
                                        {t('rating.markIncomplete')}
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            if (!showHistory) {
                                                setHistoryPage(1)
                                            }
                                            setShowHistory(!showHistory)
                                        }}
                                        className='text-[10px] font-bold px-3 py-1 rounded-full border bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                    >
                                        {showHistory ? t('rating.hideHistory') : t('rating.history')}
                                    </button>
                                </div>
                            </div>
                            {showHistory && (
                                <div className='space-y-3'>
                                    <div className='text-xs font-medium text-slate-500'>{t('rating.complaintReplies')}</div>
                                    {historyLoading ? (
                                        <div className='text-xs text-slate-500'>{t('common.loading')}</div>
                                    ) : historyReplies.length > 0 ? (
                                        <>
                                            <div className='space-y-3 max-h-52 overflow-auto pr-1'>
                                                {historyReplies.map((reply, replyIndex) => (
                                                    <div key={`${replyIndex}-${reply.createdAt}`} className='text-xs text-slate-600 bg-white rounded-lg p-3 border border-slate-200 space-y-2'>
                                                        <div className='flex items-center justify-between text-[10px] text-slate-400'>
                                                            <span className='font-medium'>{t('rating.addReply')}</span>
                                                            {reply.createdAt && (
                                                                <span>
                                                                    {new Date(reply.createdAt).toLocaleDateString(locale, options)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className='text-slate-700 leading-6'>{reply.text}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            {historyTotalPages > 1 && (
                                                <div className='flex items-center justify-between gap-3 text-[11px] text-slate-500'>
                                                    <button
                                                        type='button'
                                                        onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                                                        disabled={historyCurrentPage <= 1}
                                                        className='px-3 py-1 border rounded-lg disabled:opacity-40'
                                                    >
                                                        {t('common.previous')}
                                                    </button>
                                                    <span>{t('rating.historyPageInfo', { page: historyCurrentPage, totalPages: historyTotalPages })}</span>
                                                    <button
                                                        type='button'
                                                        onClick={() => setHistoryPage((prev) => Math.min(historyTotalPages, prev + 1))}
                                                        disabled={historyCurrentPage >= historyTotalPages}
                                                        className='px-3 py-1 border rounded-lg disabled:opacity-40'
                                                    >
                                                        {t('common.next')}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className='text-xs text-slate-500'>{t('rating.noComplaintReplies')}</div>
                                    )}
                                </div>
                            )}
                            <MwTextArea
                                id={`reply-${item._id}`}
                                label={t('rating.addReply')}
                                value={replyText}
                                onChange={(event) => setReplyText(event.target.value)}
                                rows={4}
                                placeholder={t('rating.replyPlaceholder')}
                                autoFocus={true}
                            />
                            <div className='flex justify-end'>
                                <MwButton
                                    type='saveBtn'
                                    onClick={submitReplyHandler}
                                    disabled={replyLoading || replyText.trim().length === 0}
                                >
                                    {t('rating.submitReply')}
                                </MwButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default RatingListItem
